// HST — Arbre de développement du dépôt
// <ArbreDeveloppement depot={depot} tree={DEV_TREE} selectedId={id} onPick={(id) => …} />
// Un clic sur une carte appelle onPick (la vue 3D centre la zone et ouvre sa carte de construction).
// 21/10/2026 — libellés bilingues (clés d3_* du système i18n du jeu).

import React, { useMemo, useState } from 'react';
import Icon from './Icons';
import { DEV_TREE, TIERS, TREE_LAYOUT, nodeState, unlockPath, ancestors, recommendNext, contractsNeeding, fmtEur, tierLabel } from './hstModel';
import { profileOf } from './depotProfiles';
import { useTranslation } from '../../i18n';
import './ArbreFrise.css';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n);
const COL_W = 224, ROW_H = 132, NODE_W = 204, NODE_H = 88, LEFT = 34, TOP = 16;
const FILTERS = [['all', 'd3_filter_all'], ['available', 'd3_filter_available'], ['built', 'd3_filter_built'], ['locked', 'd3_filter_locked']];
const STATE_KEYS = { built: 'd3_state_built', available: 'd3_state_available', locked: 'd3_state_locked' };

export default function ArbreDeveloppement({ depot, tree = DEV_TREE, selectedId, onPick }) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(null);
  const [filter, setFilter] = useState('all');
  const built = new Set(depot.built || []);
  const prof = profileOf(depot);
  const cols = Math.max(...Object.values(TREE_LAYOUT).map(([c]) => c)) + 1;
  const width = LEFT + cols * COL_W, height = TOP + TIERS.length * ROW_H;
  const pos = (id) => {
    const [c, r] = TREE_LAYOUT[id] || [0, 0];
    return { x: LEFT + c * COL_W + (COL_W - NODE_W) / 2, y: TOP + r * ROW_H };
  };
  const reco = recommendNext(depot, tree);
  const chain = useMemo(() => (hover ? new Set([hover, ...ancestors(hover, tree)]) : null), [hover, tree]);
  const counts = { built: 0, available: 0, locked: 0 };
  tree.forEach((n) => { counts[nodeState(n.id, built, tree, depot)]++; });

  const hoverNode = hover && tree.find((n) => n.id === hover);
  const hoverState = hover && nodeState(hover, built, tree, depot);
  const unlock = hover && hoverState === 'locked' ? unlockPath(hover, built, tree) : null;
  const plural = (n, key) => t(n > 1 ? `${key}_plural` : key, { n });

  return (
    <section className="hstx">
      <header className="hstx-head">
        <h2><Icon name="spark" /> {t('d3_tree_title')}</h2>
        <div className="hstx-filters" role="group" aria-label={t('d3_filter_steps')}>
          {FILTERS.map(([k, key]) => (
            <button key={k} className={filter === k ? 'is-on' : ''} onClick={() => setFilter(k)}>
              {t(key)}{k !== 'all' && <span>{counts[k]}</span>}
            </button>
          ))}
        </div>
      </header>

      {reco && (
        <button className="hstx-reco" onClick={() => onPick && onPick(reco.node.id)}>
          <span className="hstx-reco__ic"><Icon name={reco.node.icon} size={18} /></span>
          <span className="hstx-reco__t">
            <small>{t('d3_recommended_step')}</small>
            <b>{reco.node.id} · {reco.node.name}</b>
            <em>{reco.reason}</em>
          </span>
          <span className="hstx-reco__cost">{fmtEur(reco.node.cost)}{reco.node.monthly ? <small>+{fmt(reco.node.monthly)} €/m</small> : null}</span>
        </button>
      )}

      <div className="hstx-info" aria-live="polite">
        {hoverNode ? (
          hoverState === 'built' ? <><b>{hoverNode.name}</b> · {t('d3_state_built')} · {hoverNode.effect}</>
            : hoverState === 'available' ? <><b>{hoverNode.name}</b> · {t('d3_state_available')} · {hoverNode.effect}</>
              : <><b>{t('d3_to_unlock', { id: hoverNode.id })}</b> : {unlock.path.length > 1 ? <>{unlock.path.map((n) => n.id).join(' → ')} · <b>{fmt(unlock.cost)} €</b>{unlock.monthly ? ` · +${fmt(unlock.monthly)} €/m` : ''}</> : <>{hoverNode.condition || t('d3_conditions_unmet')}</>}{unlock.path.length > 1 && hoverNode.condition ? t('d3_condition_suffix', { c: hoverNode.condition }) : ''}</>
        ) : t('d3_hover_hint')}
      </div>

      <div className="hstx-scroll">
        <div className="hstx-tree" style={{ width, height }}>
          {TIERS.map((tier, r) => (
            <span key={tier} className="hstx-tier" style={{ top: TOP + r * ROW_H, height: NODE_H }}>{tierLabel(tier)}</span>
          ))}
          <svg className="hstx-edges" width={width} height={height} aria-hidden="true">
            {tree.flatMap((n) => (n.requires || []).map((rq) => {
              const a = pos(rq), b = pos(n.id);
              const x1 = a.x + NODE_W / 2, y1 = a.y + NODE_H, x2 = b.x + NODE_W / 2, y2 = b.y;
              const mid = y2 - 22;
              const st = built.has(n.id) ? 'built' : built.has(rq) ? 'open' : 'locked';
              const hot = chain && chain.has(n.id) && chain.has(rq);
              return <path key={rq + n.id} className={`e-${st} ${hot ? 'is-hot' : ''} ${chain && !hot ? 'is-dim' : ''}`} d={`M${x1} ${y1} V${mid} H${x2} V${y2 - 6}`} markerEnd={`url(#hstx-arrow-${st})`} />;
            }))}
            <defs>
              {['built', 'open', 'locked'].map((s) => (
                <marker key={s} id={`hstx-arrow-${s}`} viewBox="0 0 10 10" refX="5" refY="3" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
                  <path d="M0 0 L10 0 L5 6 z" className={`m-${s}`} />
                </marker>
              ))}
            </defs>
          </svg>
          {tree.map((n) => {
            const p = pos(n.id);
            const st = nodeState(n.id, built, tree, depot);
            const waiting = contractsNeeding(n.id, depot);
            const faded = (filter !== 'all' && filter !== st) || (chain && !chain.has(n.id));
            const isReco = reco && reco.node.id === n.id;
            return (
              <button
                key={n.id}
                className={`hstx-node is-${st} ${selectedId === n.id ? 'is-sel' : ''} ${faded ? 'is-faded' : ''} ${isReco ? 'is-reco' : ''}`}
                style={{ left: p.x, top: p.y, width: NODE_W, height: NODE_H }}
                onClick={() => onPick && onPick(n.id)}
                onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(n.id)} onBlur={() => setHover(null)}
                aria-label={`${n.id} ${n.name}, ${t(STATE_KEYS[st])}`}
              >
                <em className="hstx-node__id">{n.id === 'EXT' ? 'EXT' : n.id}</em>
                <span className="hstx-node__state">{st === 'built' ? <Icon name="check" size={12} /> : st === 'available' ? <Icon name="spark" size={12} /> : <Icon name="lock" size={11} />}</span>
                <span className="hstx-node__ic"><Icon name={n.icon} size={17} /></span>
                <span className="hstx-node__t">
                  <b>{n.name}</b>
                  <small>{prof.acquired.has(n.id) ? t('d3_taken_over_ex', { name: prof.rival.name }) : `${fmtEur(n.cost)}${n.monthly ? ` · ${fmt(n.monthly)} €/m` : ''}`}</small>
                </span>
                {waiting.length > 0 && (
                  <span className={`hstx-node__badge ${st === 'built' ? 'is-ok' : 'is-warn'}`}>
                    {st === 'built' ? plural(waiting.length, 'd3_serves_contracts') : plural(waiting.length, 'd3_waiting_contracts')}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      <footer className="hstx-legend">
        <span><i className="is-built" /> {t('d3_legend_built')}</span>
        <span><i className="is-available" /> {t('d3_legend_available')}</span>
        <span><i className="is-locked" /> {t('d3_legend_locked')}</span>
        <span><i className="is-reco" /> {t('d3_legend_recommended')}</span>
      </footer>
    </section>
  );
}
